import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { App } from '@/App';

// Dev only: the ngrok tunnel exists solely so Auth0 links (whose Application
// Login URI must be https) have somewhere to land — the session itself should
// live on localhost. Bounce straight there, preserving path/query/hash so
// invitation params (?invitation=…&organization=…) survive the hop. DEV-gated,
// so production builds tree-shake this entirely.
if (
    import.meta.env.DEV &&
    /\.ngrok(-free)?\.(dev|app)$/.test(window.location.hostname)
) {
    window.location.replace(
        `http://localhost:3333${window.location.pathname}${window.location.search}${window.location.hash}`
    );
}

// Global stylesheets previously loaded via Remix route `links()`.
import '@/styles/fonts.css';
import '@/styles/rc-slider.min.css';
import '@/styles/react-toastify.min.css';
import '@/styles/splide-core.min.css';

// After a deploy, open tabs still reference the previous build's hashed
// chunks, so lazy route imports 404. Vite surfaces this as `vite:preloadError`;
// reload once to pick up the new build, guarded so a genuinely missing chunk
// can't cause a reload loop.
window.addEventListener('vite:preloadError', event => {
    // NEVER auto-reload while an OAuth callback is being processed (Auth0
    // login at any path, or the Fundment/Addepar integration callbacks): the
    // `code` in the URL is single-use, so reloading mid-exchange consumes it
    // twice and kills the login. Let the error surface instead — once the SDK
    // finishes and navigates away, any still-stale chunk will re-fire this
    // event on a safe URL and the reload happens then.
    const params = new URLSearchParams(window.location.search);
    if (params.has('code') || params.has('state')) return;

    const RELOAD_KEY = 'vite-preload-error-reload-at';
    const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - lastReload < 10_000) return;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    event.preventDefault();
    window.location.reload();
});

if (import.meta.env.PROD) {
    const hostnameSplit = window.location.hostname.split('.');

    Sentry.init({
        dsn:
            import.meta.env.VITE_SENTRY_DSN ??
            'https://4a44474d9accd1d9fee35e6c5e8ec00f@o1138773.ingest.sentry.io/4506224705470464',
        release: import.meta.env.VITE_SENTRY_RELEASE,
        tracesSampleRate: 1,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1,
        attachStacktrace: true,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],
        environment: hostnameSplit.length ? hostnameSplit[0] : 'not-provided',
    });
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

createRoot(rootEl).render(
    <StrictMode>
        <App />
    </StrictMode>
);
